import React, { useState, useEffect, useCallback } from "react";
import { Settings as SettingsIcon, Power, Pause, Play, Zap, Globe, Key, Copy, Check, RefreshCw, Plug, AlertTriangle, Eye, EyeOff, Shield, HelpCircle, Building, Phone, Mail, MapPin, Award, Bell } from "lucide-react";
import api from "../api/client";

function Toggle({ label, checked, onChange, description }) {
  return (
    <label className="flex items-start justify-between cursor-pointer group">
      <div>
        <div className="text-sm text-gray-200 group-hover:text-white">{label}</div>
        {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
      </div>
      <div className={`relative w-10 h-5 rounded-full transition-colors ${checked ? "bg-copper" : "bg-dark-50"}`} onClick={onChange}>
        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </div>
    </label>
  );
}

function ThresholdSlider({ value, onChange }) {
  const label = value <= 45 ? "Auto-push most fixes" : value <= 55 ? "Balanced" : value <= 85 ? "Conservative" : "Only sure wins";
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-300">Auto-push confidence threshold</span>
        <span className="text-sm font-mono text-copper">{value}% <span className="text-gray-500 text-xs">({label})</span></span>
      </div>
      <input
        type="range" min={40} max={100} value={value} onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full accent-copper h-1.5 bg-dark-50 rounded-full appearance-none cursor-pointer"
      />
      <div className="flex justify-between text-xs text-gray-600 mt-1">
        <span>40% Auto-push most fixes</span>
        <span>Balanced</span>
        <span>Conservative</span>
        <span>100% Only sure wins</span>
      </div>
    </div>
  );
}

function WidthSlider({ value, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-300">Content width</span>
        <span className="text-sm font-mono text-copper">{value}px</span>
      </div>
      <input
        type="range" min={800} max={1400} step={50} value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full accent-copper h-1.5 bg-dark-50 rounded-full appearance-none cursor-pointer"
      />
      <div className="flex justify-between text-xs text-gray-600 mt-1">
        <span>800px (narrow)</span>
        <span>1400px (wide)</span>
      </div>
    </div>
  );
}

function PluginConnection({ site }) {
  const [maskedKey, setMaskedKey] = useState(null);
  const [hasKey, setHasKey] = useState(site.has_api_key || false);
  const [fullKey, setFullKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const loadKey = useCallback(() => {
    api.get(`/websites/${site.id}/api-key/`).then((res) => {
      setHasKey(res.data.has_key);
      setMaskedKey(res.data.masked_key);
    }).catch(() => {});
  }, [site.id]);

  useEffect(() => { loadKey(); }, [loadKey]);

  const generateKey = () => {
    setGenerating(true);
    setFullKey(null);
    setTestResult(null);
    setConfirmRegen(false);
    api.post(`/websites/${site.id}/generate-api-key/`).then((res) => {
      setFullKey(res.data.key);
      setHasKey(true);
      setMaskedKey(res.data.key.slice(0, 8) + "••••••••" + res.data.key.slice(-8));
    }).catch(() => {}).finally(() => setGenerating(false));
  };

  const copyKey = () => {
    const key = fullKey || maskedKey;
    if (key) {
      navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const testConnection = () => {
    setTesting(true);
    setTestResult(null);
    api.post(`/websites/${site.id}/test-plugin-connection/`).then((res) => {
      setTestResult(res.data);
    }).catch((err) => {
      setTestResult({ connected: false, error: err.response?.data?.error || "Request failed" });
    }).finally(() => setTesting(false));
  };

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${hasKey ? "bg-copper" : "bg-gray-600"}`} />
          <div>
            <h3 className="text-sm font-semibold">{site.name}</h3>
            <div className="text-xs text-gray-500">{site.domain}</div>
          </div>
        </div>
        {hasKey && (
          <button
            onClick={testConnection}
            disabled={testing}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-dark-50 text-gray-300 hover:border-copper/50 hover:text-copper disabled:opacity-50"
          >
            <Plug size={12} className={testing ? "animate-spin" : ""} />
            {testing ? "Testing..." : "Test Connection"}
          </button>
        )}
      </div>

      <div className="space-y-4 pt-2 border-t border-dark-50">
        {/* API Key display */}
        {!hasKey ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Key size={14} />
              <span>No API key generated</span>
            </div>
            <button
              onClick={generateKey}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-copper/20 text-copper hover:bg-copper/30 disabled:opacity-50"
            >
              <Key size={14} />
              {generating ? "Generating..." : "Generate API Key"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Show full key after generation */}
            {fullKey && (
              <div className="bg-green-900/20 border border-green-800/50 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs text-green-400 font-medium">
                  <AlertTriangle size={12} />
                  Copy this key now — it won't be shown in full again
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-dark/60 rounded px-3 py-2 text-sm font-mono text-green-300 select-all break-all">
                    {fullKey}
                  </code>
                  <button
                    onClick={copyKey}
                    className="flex items-center gap-1 px-3 py-2 text-xs rounded-lg bg-green-800/40 text-green-300 hover:bg-green-800/60 shrink-0"
                  >
                    {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                  </button>
                </div>
              </div>
            )}

            {/* Masked key display */}
            {!fullKey && maskedKey && (
              <div className="flex items-center gap-2">
                <Key size={14} className="text-gray-500 shrink-0" />
                <code className="flex-1 bg-dark/60 rounded px-3 py-2 text-sm font-mono text-gray-400">
                  {maskedKey}
                </code>
              </div>
            )}

            {/* Regenerate button */}
            {!confirmRegen ? (
              <button
                onClick={() => setConfirmRegen(true)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-amber-400"
              >
                <RefreshCw size={12} /> Regenerate key
              </button>
            ) : (
              <div className="flex items-center gap-2 bg-amber-900/20 border border-amber-800/50 rounded-lg p-3">
                <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                <span className="text-xs text-amber-300 flex-1">This will invalidate the current key. The plugin will disconnect until you enter the new key.</span>
                <button
                  onClick={generateKey}
                  disabled={generating}
                  className="px-3 py-1 text-xs rounded bg-amber-800/50 text-amber-300 hover:bg-amber-800/70 shrink-0"
                >
                  {generating ? "..." : "Confirm"}
                </button>
                <button
                  onClick={() => setConfirmRegen(false)}
                  className="px-3 py-1 text-xs rounded bg-dark-50 text-gray-400 hover:text-gray-200 shrink-0"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Connection test result */}
        {testResult && (
          <div className={`flex items-center gap-2 rounded-lg p-3 text-xs ${
            testResult.connected
              ? "bg-green-900/20 border border-green-800/50 text-green-400"
              : "bg-red-900/20 border border-red-800/50 text-red-400"
          }`}>
            <div className={`w-2 h-2 rounded-full shrink-0 ${testResult.connected ? "bg-green-400" : "bg-red-400"}`} />
            {testResult.connected ? "Connected — plugin is responding" : `Not connected — ${testResult.error}`}
          </div>
        )}

        {/* Setup instructions */}
        <div className="bg-dark/40 rounded-lg p-3 space-y-2">
          <div className="text-xs font-semibold text-gray-400">Setup Instructions</div>
          <ol className="text-xs text-gray-500 space-y-1 list-decimal list-inside">
            <li>Install the <span className="text-gray-300">OnesuiteSEO</span> plugin on your WordPress site</li>
            <li>Go to <span className="text-gray-300">OnesuiteSEO &gt; Settings</span> in WordPress admin</li>
            <li>Paste the API key above and set the platform URL to <span className="text-copper font-mono">app.gcmmgroup.com</span></li>
            <li>Click <span className="text-gray-300">Connect</span> and verify the connection</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

function WPCredentials({ site }) {
  const [wpUrl, setWpUrl] = useState(site.wp_api_url || "");
  const [wpUser, setWpUser] = useState(site.wp_username || "");
  const [wpPass, setWpPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const hasCredentials = !!(site.wp_api_url && site.wp_username);

  const handleSave = () => {
    setSaving(true);
    setSaved(false);
    setTestResult(null);
    const data = { wp_api_url: wpUrl, wp_username: wpUser };
    if (wpPass) data.wp_app_password = wpPass;
    api.patch(`/websites/${site.id}/`, data)
      .then(() => { setSaved(true); setTimeout(() => setSaved(false), 3000); })
      .catch(() => setTestResult({ status: "failed", message: "Failed to save credentials" }))
      .finally(() => setSaving(false));
  };

  const handleTest = () => {
    setTesting(true);
    setTestResult(null);
    // Save first, then test
    const data = { wp_api_url: wpUrl, wp_username: wpUser };
    if (wpPass) data.wp_app_password = wpPass;
    api.patch(`/websites/${site.id}/`, data)
      .then(() => api.post(`/websites/${site.id}/test-wp-connection/`))
      .then((res) => setTestResult(res.data))
      .catch((err) => setTestResult({ status: "failed", message: err.response?.data?.message || "Request failed" }))
      .finally(() => setTesting(false));
  };

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${hasCredentials ? "bg-copper" : "bg-gray-600"}`} />
          <div>
            <h3 className="text-sm font-semibold">{site.name}</h3>
            <div className="text-xs text-gray-500">{site.domain}</div>
          </div>
        </div>
        {hasCredentials && (
          <div className="flex items-center gap-1 text-xs text-copper">
            <Shield size={12} />
            <span>Credentials saved</span>
          </div>
        )}
      </div>

      <div className="space-y-4 pt-2 border-t border-dark-50">
        <div>
          <label className="text-sm text-gray-300 block mb-1">WordPress Site URL</label>
          <input
            type="text"
            value={wpUrl}
            onChange={(e) => setWpUrl(e.target.value)}
            placeholder={`https://${site.domain}/wp-json`}
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
          />
          <div className="text-xs text-gray-600 mt-1">Usually: https://yourdomain.com/wp-json</div>
        </div>
        <div>
          <label className="text-sm text-gray-300 block mb-1">WordPress Username</label>
          <input
            type="text"
            value={wpUser}
            onChange={(e) => setWpUser(e.target.value)}
            placeholder="admin"
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
          />
        </div>
        <div>
          <label className="text-sm text-gray-300 block mb-1">Application Password</label>
          <div className="relative">
            <input
              type={showPass ? "text" : "password"}
              value={wpPass}
              onChange={(e) => setWpPass(e.target.value)}
              placeholder={hasCredentials ? "••••••••••••••••••••••••" : "Paste your app password here"}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
            >
              {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          {hasCredentials && !wpPass && (
            <div className="text-xs text-gray-600 mt-1">Leave empty to keep existing password</div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving || !wpUrl || !wpUser}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-copper/20 text-copper hover:bg-copper/30 disabled:opacity-50"
          >
            {saving ? "Saving..." : saved ? <><Check size={14} /> Saved</> : "Save Credentials"}
          </button>
          <button
            onClick={handleTest}
            disabled={testing || !wpUrl || !wpUser}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-dark-50 text-gray-300 hover:border-copper/50 hover:text-copper disabled:opacity-50"
          >
            <Plug size={14} className={testing ? "animate-spin" : ""} />
            {testing ? "Testing..." : "Test Connection"}
          </button>
        </div>

        {testResult && (
          <div className={`flex items-center gap-2 rounded-lg p-3 text-xs ${
            testResult.status === "success"
              ? "bg-green-900/20 border border-green-800/50 text-green-400"
              : testResult.status === "warning"
              ? "bg-amber-900/20 border border-amber-800/50 text-amber-400"
              : "bg-red-900/20 border border-red-800/50 text-red-400"
          }`}>
            <div className={`w-2 h-2 rounded-full shrink-0 ${
              testResult.status === "success" ? "bg-green-400"
              : testResult.status === "warning" ? "bg-amber-400"
              : "bg-red-400"
            }`} />
            {testResult.status === "success" ? `✓ ${testResult.message}`
              : testResult.status === "warning" ? `⚠ ${testResult.message}`
              : `✗ ${testResult.message}`}
          </div>
        )}

        {/* Setup instructions */}
        <details className="bg-dark/40 rounded-lg p-3">
          <summary className="text-xs font-semibold text-gray-400 cursor-pointer flex items-center gap-1">
            <HelpCircle size={12} /> How to create an Application Password
          </summary>
          <ol className="text-xs text-gray-500 space-y-1 list-decimal list-inside mt-2">
            <li>Log into your WordPress admin at <span className="text-gray-300 font-mono">{site.domain}/wp-admin</span></li>
            <li>Go to <span className="text-gray-300">Users → Your Profile</span></li>
            <li>Scroll down to <span className="text-gray-300">"Application Passwords"</span></li>
            <li>Enter name <span className="text-copper">"OnesuiteSEO"</span> → click <span className="text-gray-300">Add New Application Password</span></li>
            <li>Copy the generated password and paste it above</li>
          </ol>
        </details>
      </div>
    </div>
  );
}

function GSCConnection({ site }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get(`/websites/${site.id}/gsc/status/`)
      .then((res) => setStatus(res.data))
      .catch(() => setStatus({ gsc_connected: false }))
      .finally(() => setLoading(false));
  }, [site.id]);

  const [connectError, setConnectError] = useState(null);

  const refreshStatus = () => {
    api.get(`/websites/${site.id}/gsc/status/`)
      .then((res) => setStatus(res.data))
      .catch(() => {});
  };

  const handleConnect = () => {
    setConnecting(true);
    setConnectError(null);
    api.post(`/websites/${site.id}/gsc/connect/`)
      .then((res) => {
        if (res.data.auth_url) {
          const popup = window.open(res.data.auth_url, "_blank", "width=600,height=700");
          // Poll for popup close — when user finishes OAuth, refresh status
          const check = setInterval(() => {
            if (!popup || popup.closed) {
              clearInterval(check);
              setConnecting(false);
              refreshStatus();
            }
          }, 1000);
        } else {
          setConnecting(false);
        }
      })
      .catch((err) => {
        setConnectError(err.response?.data?.error || "Connection failed");
        setConnecting(false);
      });
  };

  const handleSync = () => {
    setSyncing(true);
    api.post(`/websites/${site.id}/gsc/sync/`)
      .then(() => {
        setTimeout(() => {
          api.get(`/websites/${site.id}/gsc/status/`)
            .then((r) => setStatus(r.data))
            .catch(() => {});
          setSyncing(false);
        }, 10000);
      })
      .catch(() => setSyncing(false));
  };

  const handleDisconnect = () => {
    api.post(`/websites/${site.id}/gsc/disconnect/`)
      .then(() => setStatus({ gsc_connected: false }))
      .catch(() => {});
  };

  if (loading) return null;

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${status?.gsc_connected ? "bg-green-400" : "bg-gray-600"}`} />
          <div>
            <h3 className="text-sm font-semibold">Google Search Console</h3>
            <div className="text-xs text-gray-500">{site.domain}</div>
          </div>
        </div>
      </div>

      {status?.gsc_connected ? (
        <div className="space-y-3 pt-2 border-t border-dark-50">
          <div className="flex items-center gap-2 text-xs">
            <Check size={12} className="text-green-400" />
            <span className="text-green-400">Connected to {status.gsc_property_url || site.domain}</span>
          </div>
          {status.gsc_last_synced && (
            <div className="text-xs text-gray-500">
              Last synced: {new Date(status.gsc_last_synced).toLocaleString()}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button onClick={handleSync} disabled={syncing}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 disabled:opacity-50">
              <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing..." : "Sync Now"}
            </button>
            <button onClick={handleDisconnect}
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-500 hover:text-red-400 transition-colors">
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="pt-2 border-t border-dark-50">
          <button onClick={handleConnect} disabled={connecting || !status?.google_oauth_configured}
            title={!status?.google_oauth_configured ? "Configure Google credentials in Admin first" : ""}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-copper/20 text-copper rounded-lg hover:bg-copper/30 disabled:opacity-50 disabled:cursor-not-allowed">
            <Globe size={14} />
            {connecting ? "Connecting..." : "Connect Google Search Console"}
          </button>
          {connectError && (
            <div className="flex items-center gap-1.5 text-xs text-red-400 mt-2">
              <AlertTriangle size={12} />
              <span>{connectError}</span>
            </div>
          )}
          {!status?.google_oauth_configured ? (
            <div className="flex items-center gap-1.5 text-xs text-amber-400 mt-2">
              <AlertTriangle size={12} />
              <span>Google OAuth not configured. Go to <strong>Admin → API Configuration</strong> to set up Google Client ID and Secret.</span>
            </div>
          ) : (
            <p className="text-xs text-gray-600 mt-2">
              Requires Google OAuth. You'll be redirected to Google to authorize access.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function BusinessInfo({ site }) {
  const [form, setForm] = useState({
    business_name: site.business_name || "",
    phone_number: site.phone_number || "",
    contact_email: site.contact_email || "",
    address: site.address || "",
    service_areas: site.service_areas || "",
    certifications: site.certifications || "",
    unique_selling_points: site.unique_selling_points || "",
    company_tagline: site.company_tagline || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaving(true);
    setSaved(false);
    api.patch(`/websites/${site.id}/`, form)
      .then(() => { setSaved(true); setTimeout(() => setSaved(false), 3000); })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const field = (key, label, icon, placeholder, type = "input") => (
    <div>
      <label className="text-sm text-gray-300 flex items-center gap-1.5 mb-1">{icon}{label}</label>
      {type === "textarea" ? (
        <textarea
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          placeholder={placeholder}
          rows={2}
          className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50 resize-none"
        />
      ) : (
        <input
          type="text"
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          placeholder={placeholder}
          className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
        />
      )}
    </div>
  );

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-2.5 h-2.5 rounded-full bg-copper" />
        <div>
          <h3 className="text-sm font-semibold">{site.name}</h3>
          <div className="text-xs text-gray-500">{site.domain}</div>
        </div>
      </div>
      <div className="space-y-4 pt-2 border-t border-dark-50">
        <div className="grid grid-cols-2 gap-4">
          {field("business_name", "Business Name", <Building size={13} />, "GCMM Home Improvement LLC")}
          {field("phone_number", "Phone", <Phone size={13} />, "(347) 961-7357")}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {field("contact_email", "Email", <Mail size={13} />, "gary@gcmm.nyc")}
          {field("company_tagline", "Tagline", null, "New York's Premier Contractor")}
        </div>
        {field("address", "Address", <MapPin size={13} />, "39 Miller Ave, Tarrytown, NY")}
        {field("service_areas", "Service Areas", <Globe size={13} />, "Westchester County, Bronx, NYC, Yonkers", "textarea")}
        {field("certifications", "Certifications", <Award size={13} />, "Licensed & Insured, A-dec Certified", "textarea")}
        {field("unique_selling_points", "Unique Selling Points", null, "20+ years experience, manufacturer-trained", "textarea")}
        <div className="bg-dark/40 rounded-lg p-3 text-xs text-gray-500">
          These details are automatically included in all AI-generated content (landing pages, blog posts, topic clusters, syndication). The AI will use the exact phone number, address, and business name — never placeholder data.
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-copper/20 text-copper hover:bg-copper/30 disabled:opacity-50"
        >
          {saving ? "Saving..." : saved ? <><Check size={14} /> Saved</> : "Save Business Info"}
        </button>
      </div>
    </div>
  );
}

export default function SettingsPage({ websites }) {
  const [configs, setConfigs] = useState({});
  const [saving, setSaving] = useState("");

  useEffect(() => {
    if (!websites?.length) return;
    websites.forEach((site) => {
      api.get(`/websites/${site.id}/autopilot/`)
        .then((res) => setConfigs((prev) => ({ ...prev, [site.id]: res.data })))
        .catch(() => {});
    });
  }, [websites]);

  const updateConfig = (siteId, field, value) => {
    setConfigs((prev) => ({
      ...prev,
      [siteId]: { ...prev[siteId], [field]: value },
    }));
    setSaving(siteId + field);
    api.put(`/websites/${siteId}/autopilot/`, { [field]: value })
      .finally(() => setTimeout(() => setSaving(""), 500));
  };

  const updateWebsite = (siteId, field, value) => {
    api.patch(`/websites/${siteId}/`, { [field]: value }).catch(() => {});
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <SettingsIcon size={20} className="text-copper" /> Settings
      </h2>

      {/* WordPress Plugin Connection — shown first for discoverability */}
      <h3 className="text-md font-semibold flex items-center gap-2">
        <Plug size={18} className="text-copper" /> WordPress Plugin Connection
      </h3>
      <div className="space-y-4">
        {(websites || []).map((site) => (
          <PluginConnection key={`plugin-${site.id}`} site={site} />
        ))}
      </div>

      {/* WordPress Credentials */}
      <h3 className="text-md font-semibold flex items-center gap-2 mt-8">
        <Shield size={18} className="text-copper" /> WordPress Credentials
      </h3>
      <p className="text-xs text-gray-500 -mt-2">Required for OnesuiteSEO to push content, fix meta tags, rename images, and manage pages on your WordPress sites.</p>
      <div className="space-y-4">
        {(websites || []).map((site) => (
          <WPCredentials key={`wp-creds-${site.id}`} site={site} />
        ))}
      </div>

      {/* Business Info */}
      <h3 className="text-md font-semibold flex items-center gap-2 mt-8">
        <Building size={18} className="text-copper" /> Business Info
      </h3>
      <p className="text-xs text-gray-500 -mt-2">Business details used in all AI-generated content. The AI will use the exact phone number, address, and company name — never placeholder data.</p>
      <div className="space-y-4">
        {(websites || []).map((site) => (
          <BusinessInfo key={`biz-${site.id}`} site={site} />
        ))}
      </div>

      {/* Google Integrations */}
      <h3 className="text-md font-semibold flex items-center gap-2 mt-8">
        <Globe size={18} className="text-copper" /> Google Integrations
      </h3>
      <div className="space-y-4">
        {(websites || []).map((site) => (
          <GSCConnection key={`gsc-${site.id}`} site={site} />
        ))}
      </div>

      {/* Auto-Pilot Settings */}
      <h3 className="text-md font-semibold flex items-center gap-2 mt-8">
        <Zap size={18} className="text-copper" /> Auto-Pilot
      </h3>
      <div className="space-y-4">
        {(websites || []).map((site) => {
          const config = configs[site.id] || {};
          const wpConnected = site.wp_connected ?? !!(site.wp_api_url && site.wp_username);
          const isActive = wpConnected && config.autopilot_enabled && !config.autopilot_paused;
          const isPaused = wpConnected && config.autopilot_enabled && config.autopilot_paused;

          return (
            <div key={site.id} className={`bg-dark-300 border rounded-xl p-6 space-y-5 ${!wpConnected ? "border-red-900/50 opacity-80" : "border-dark-50"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${!wpConnected ? "bg-red-500" : isActive ? "bg-green-400" : isPaused ? "bg-amber-400" : "bg-gray-600"}`} />
                  <div>
                    <h3 className="text-sm font-semibold">{site.name}</h3>
                    <div className="text-xs text-gray-500">{site.domain}</div>
                  </div>
                </div>
                {!wpConnected ? (
                  <div className="flex items-center gap-1 text-xs text-red-400">
                    <AlertTriangle size={12} />
                    WordPress not connected
                  </div>
                ) : (
                <div className="flex items-center gap-2">
                  {config.autopilot_enabled && (
                    <button
                      onClick={() => updateConfig(site.id, "autopilot_paused", !config.autopilot_paused)}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border ${
                        config.autopilot_paused
                          ? "border-green-800 text-green-400 hover:bg-green-900/30"
                          : "border-amber-800 text-amber-400 hover:bg-amber-900/30"
                      }`}
                    >
                      {config.autopilot_paused ? <><Play size={12} /> Resume</> : <><Pause size={12} /> Pause</>}
                    </button>
                  )}
                  <button
                    onClick={() => updateConfig(site.id, "autopilot_enabled", !config.autopilot_enabled)}
                    className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg font-medium ${
                      config.autopilot_enabled
                        ? "bg-red-900/30 text-red-400 hover:bg-red-900/50"
                        : "bg-copper/20 text-copper hover:bg-copper/30"
                    }`}
                  >
                    <Power size={12} />
                    {config.autopilot_enabled ? "Turn Off" : "Enable Auto-Pilot"}
                  </button>
                </div>
                )}
              </div>

              {!wpConnected && (
                <div className="flex items-center gap-2 bg-red-900/20 border border-red-800/50 rounded-lg p-3 text-xs text-red-400">
                  <AlertTriangle size={14} className="shrink-0" />
                  <span>Configure WordPress credentials in the <strong>WordPress Credentials</strong> section above to enable Auto-Pilot for this site.</span>
                </div>
              )}

              {wpConnected && config.autopilot_enabled && (
                <div className="space-y-4 pt-2 border-t border-dark-50">
                  <ThresholdSlider
                    value={config.autopilot_auto_push_threshold || 60}
                    onChange={(v) => updateConfig(site.id, "autopilot_auto_push_threshold", v)}
                  />
                  <Toggle
                    label="Auto-generate landing pages"
                    description="Create pages for missing keywords found in competitor analysis"
                    checked={config.autopilot_auto_generate_pages || false}
                    onChange={() => updateConfig(site.id, "autopilot_auto_generate_pages", !config.autopilot_auto_generate_pages)}
                  />
                  <Toggle
                    label="Auto-publish generated pages"
                    description="Automatically publish generated pages (use with caution)"
                    checked={config.autopilot_auto_publish_pages || false}
                    onChange={() => updateConfig(site.id, "autopilot_auto_publish_pages", !config.autopilot_auto_publish_pages)}
                  />
                  <Toggle
                    label="Weekly email report"
                    description="Send weekly summary email every Sunday"
                    checked={config.autopilot_weekly_email !== false}
                    onChange={() => updateConfig(site.id, "autopilot_weekly_email", !config.autopilot_weekly_email)}
                  />

                  <div className="bg-dark/40 rounded-lg p-3">
                    <div className="text-xs font-semibold text-gray-400 mb-2 flex items-center gap-1">
                      <Zap size={12} className="text-copper" /> Auto-Pilot Schedule
                      <span className="text-gray-600 font-normal ml-1">Click to toggle</span>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-[10px] text-center">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                        <div key={d} className="text-gray-500 font-medium">{d}</div>
                      ))}
                      {[
                        { field: "autopilot_monday_gsc", label: "GSC + Audit" },
                        { field: "autopilot_tuesday_seo_scan", label: "SEO Scan" },
                        { field: "autopilot_wednesday_autofix", label: "Auto-Fix" },
                        { field: "autopilot_thursday_competitors", label: "Competitors" },
                        { field: "autopilot_friday_pages", label: "Pages" },
                        { field: "autopilot_saturday_report", label: "Report" },
                        { field: "autopilot_sunday_email", label: "Email" },
                      ].map(({ field, label }) => {
                        const active = config[field] !== false;
                        return (
                          <button
                            key={field}
                            type="button"
                            onClick={() => updateConfig(site.id, field, !active)}
                            className={`rounded px-1 py-0.5 transition-colors cursor-pointer ${
                              active
                                ? "bg-green-900/30 text-green-400 hover:bg-green-900/50"
                                : "bg-dark-50 text-gray-600 hover:bg-dark-50/80"
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* WordPress Publishing Settings */}
      <h3 className="text-md font-semibold flex items-center gap-2 mt-8">
        <Globe size={18} className="text-copper" /> WordPress Publishing Settings
      </h3>
      <div className="space-y-4">
        {(websites || []).map((site) => (
          <div key={`wp-${site.id}`} className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />
              <div>
                <h3 className="text-sm font-semibold">{site.name}</h3>
                <div className="text-xs text-gray-500">{site.domain}</div>
              </div>
            </div>
            <div className="space-y-4 pt-2 border-t border-dark-50">
              <WidthSlider
                value={site.wp_content_width || 1100}
                onChange={(v) => updateWebsite(site.id, "wp_content_width", v)}
              />
              <div>
                <label className="text-sm text-gray-300 block mb-1">Page template slug</label>
                <input
                  type="text"
                  defaultValue={site.wp_page_template || ""}
                  onBlur={(e) => updateWebsite(site.id, "wp_page_template", e.target.value)}
                  placeholder="e.g. page-full-width"
                  className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
                />
              </div>
              <Toggle
                label="Publish immediately"
                description='When enabled, pages go live immediately instead of as drafts'
                checked={(site.wp_publish_status || "draft") === "publish"}
                onChange={() => updateWebsite(site.id, "wp_publish_status", site.wp_publish_status === "publish" ? "draft" : "publish")}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-300 block mb-1">Parent page ID</label>
                  <input
                    type="number"
                    defaultValue={site.wp_parent_page_id || ""}
                    onBlur={(e) => updateWebsite(site.id, "wp_parent_page_id", e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Optional"
                    className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 block mb-1">Default category ID</label>
                  <input
                    type="number"
                    defaultValue={site.wp_category_id || ""}
                    onBlur={(e) => updateWebsite(site.id, "wp_category_id", e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Optional"
                    className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Notification Preferences */}
      <NotificationPreferences />
    </div>
  );
}

function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/notifications/preferences/")
      .then((res) => setPrefs(res.data))
      .catch(() => setPrefs({}));

    // Check push subscription state
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker.ready.then((reg) =>
        reg.pushManager.getSubscription().then((sub) => setPushSubscribed(!!sub))
      ).catch(() => {});
    }
  }, []);

  const updatePref = (field, value) => {
    const updated = { ...prefs, [field]: value };
    setPrefs(updated);
    setSaving(true);
    api.put("/notifications/preferences/", { [field]: value })
      .finally(() => setTimeout(() => setSaving(false), 300));
  };

  const togglePush = async () => {
    try {
      const { subscribeToPush, unsubscribeFromPush } = await import("../utils/push");
      if (pushSubscribed) {
        await unsubscribeFromPush();
        setPushSubscribed(false);
        updatePref("push_enabled", false);
      } else {
        const ok = await subscribeToPush();
        setPushSubscribed(ok);
        if (ok) updatePref("push_enabled", true);
      }
    } catch (err) {
      console.warn("Push toggle failed:", err);
    }
  };

  if (!prefs) return null;

  const categories = [
    { field: "content_enabled", label: "Content", description: "Content generation and publishing" },
    { field: "keywords_enabled", label: "Keywords", description: "Keyword rank changes and alerts" },
    { field: "audit_enabled", label: "Site Audit", description: "Audit completion and issues" },
    { field: "backlinks_enabled", label: "Backlinks", description: "New and lost backlinks" },
    { field: "syndication_enabled", label: "Syndication", description: "Content syndication status" },
    { field: "local_rankings_enabled", label: "Local Rankings", description: "Local rank scan results" },
    { field: "pages_enabled", label: "Landing Pages", description: "Page creation and publishing" },
    { field: "leads_enabled", label: "Leads", description: "New lead captures" },
    { field: "wordpress_enabled", label: "WordPress", description: "WordPress sync and push events" },
    { field: "system_enabled", label: "System", description: "System alerts and updates" },
  ];

  return (
    <>
      <h3 className="text-md font-semibold flex items-center gap-2 mt-8">
        <Bell size={18} className="text-copper" /> Notification Preferences
      </h3>
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-5">
        {/* Push toggle */}
        <Toggle
          label="Push Notifications"
          description={pushSubscribed ? "Browser push notifications are enabled" : "Enable browser push notifications"}
          checked={pushSubscribed}
          onChange={togglePush}
        />

        {/* Quiet hours */}
        <div className="pt-3 border-t border-dark-50">
          <div className="text-sm text-gray-300 mb-2">Quiet Hours</div>
          <div className="flex items-center gap-3">
            <input
              type="time"
              value={prefs.quiet_start || ""}
              onChange={(e) => updatePref("quiet_start", e.target.value || null)}
              className="bg-dark border border-dark-50 rounded-lg px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-copper/50"
            />
            <span className="text-gray-500 text-sm">to</span>
            <input
              type="time"
              value={prefs.quiet_end || ""}
              onChange={(e) => updatePref("quiet_end", e.target.value || null)}
              className="bg-dark border border-dark-50 rounded-lg px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-copper/50"
            />
            <span className="text-xs text-gray-600">(no push during these hours)</span>
          </div>
        </div>

        {/* Category toggles */}
        <div className="pt-3 border-t border-dark-50 space-y-3">
          <div className="text-sm text-gray-300 mb-1">Categories</div>
          {categories.map(({ field, label, description }) => (
            <Toggle
              key={field}
              label={label}
              description={description}
              checked={prefs[field] !== false}
              onChange={() => updatePref(field, !prefs[field])}
            />
          ))}
        </div>
      </div>
    </>
  );
}
